/**
 * Star Ratings Data Model
 * Tracks Aged Care Star Ratings components
 * Four components: Experience (33%), Compliance (30%), Staffing (22%), Quality (15%)
 */

export const createStarRating = (data = {}) => {
  return {
    id: data.id || `sr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    facilityId: data.facilityId || null,
    
    // Date
    assessmentDate: data.assessmentDate || new Date().toISOString().split('T')[0],
    effectiveDate: data.effectiveDate || new Date().toISOString().split('T')[0],
    
    // Component 1: Residents' Experience (33%)
    residentsExperience: {
      score: data.residentsExperience?.score || 0, // 0-5 stars
      weight: 0.33,
      sources: {
        consumerSurvey: data.residentsExperience?.sources?.consumerSurvey || 0,
        familyFeedback: data.residentsExperience?.sources?.familyFeedback || 0,
        satisfactionMetrics: data.residentsExperience?.sources?.satisfactionMetrics || 0
      },
      lastUpdated: data.residentsExperience?.lastUpdated || new Date().toISOString()
    },
    
    // Component 2: Compliance (30%)
    compliance: {
      score: data.compliance?.score || 0, // 0-5 stars
      weight: 0.30,
      sources: {
        qualityStandards: data.compliance?.sources?.qualityStandards || 0,
        auditResults: data.compliance?.sources?.auditResults || 0,
        sirsCompliance: data.compliance?.sources?.sirsCompliance || 0
      },
      lastUpdated: data.compliance?.lastUpdated || new Date().toISOString(),
      // Critical: 1 or 2 star Compliance caps overall rating
      capsOverall: data.compliance?.score <= 2
    },
    
    // Component 3: Staffing (22%)
    staffing: {
      score: data.staffing?.score || 0, // 0-5 stars
      weight: 0.22,
      sources: {
        careMinutesCompliance: data.staffing?.sources?.careMinutesCompliance || 0,
        rnMinutesCompliance: data.staffing?.sources?.rnMinutesCompliance || 0,
        staffingLevels: data.staffing?.sources?.staffingLevels || 0,
        turnoverRate: data.staffing?.sources?.turnoverRate || 0
      },
      lastUpdated: data.staffing?.lastUpdated || new Date().toISOString(),
      // Critical: Must meet BOTH total care minutes AND RN care minutes
      meetsBothTargets: data.staffing?.meetsBothTargets ?? false
    },
    
    // Component 4: Quality Measures (15%)
    qualityMeasures: {
      score: data.qualityMeasures?.score || 0, // 0-5 stars
      weight: 0.15,
      sources: {
        nqipIndicators: data.qualityMeasures?.sources?.nqipIndicators || 0,
        clinicalMetrics: data.qualityMeasures?.sources?.clinicalMetrics || 0,
        safetyIndicators: data.qualityMeasures?.sources?.safetyIndicators || 0
      },
      lastUpdated: data.qualityMeasures?.lastUpdated || new Date().toISOString()
    },
    
    // Overall Star Rating
    overallRating: data.overallRating || 0, // 0-5 stars (calculated)
    
    // Benchmark Comparison
    benchmarks: {
      national: data.benchmarks?.national || 0,
      state: data.benchmarks?.state || 0,
      peerGroup: data.benchmarks?.peerGroup || 0
    },
    
    // Metadata
    calculatedAt: data.calculatedAt || new Date().toISOString(),
    calculatedBy: data.calculatedBy || 'system',
    notes: data.notes || null
  };
};

/**
 * Calculate overall star rating from components
 */
export const calculateOverallRating = (starRating) => {
  // Weighted average of components
  const weightedSum = 
    (starRating.residentsExperience.score * starRating.residentsExperience.weight) +
    (starRating.compliance.score * starRating.compliance.weight) +
    (starRating.staffing.score * starRating.staffing.weight) +
    (starRating.qualityMeasures.score * starRating.qualityMeasures.weight);
  
  // Round to nearest 0.5 stars
  let overallRating = Math.round(weightedSum * 2) / 2;
  
  // Critical rule: If Compliance is 1 or 2 stars, cap overall at 2 stars
  if (starRating.compliance.score <= 2) {
    overallRating = Math.min(overallRating, 2);
  }
  
  return overallRating;
};

/**
 * Calculate component score from sources
 */
export const calculateComponentScore = (component) => {
  const sources = component.sources || {};
  const values = Object.values(sources).filter(v => typeof v === 'number');
  
  if (values.length === 0) return 0;
  
  // Average of source scores
  const average = values.reduce((sum, val) => sum + val, 0) / values.length;
  
  // Round to nearest 0.5 stars
  return Math.round(average * 2) / 2;
};

/**
 * Calculate Residents' Experience score
 */
export const calculateResidentsExperienceScore = (surveyData, familyFeedback, satisfactionMetrics) => {
  const scores = [];
  
  if (surveyData && typeof surveyData === 'number') scores.push(surveyData);
  if (familyFeedback && typeof familyFeedback === 'number') scores.push(familyFeedback);
  if (satisfactionMetrics && typeof satisfactionMetrics === 'number') scores.push(satisfactionMetrics);
  
  if (scores.length === 0) return 0;
  
  const average = scores.reduce((sum, val) => sum + val, 0) / scores.length;
  return Math.round(average * 2) / 2;
};

/**
 * Calculate Compliance score
 */
export const calculateComplianceScore = (qualityStandards, auditResults, sirsCompliance) => {
  const scores = [];
  
  if (qualityStandards && typeof qualityStandards === 'number') scores.push(qualityStandards);
  if (auditResults && typeof auditResults === 'number') scores.push(auditResults);
  if (sirsCompliance && typeof sirsCompliance === 'number') scores.push(sirsCompliance);
  
  if (scores.length === 0) return 0;
  
  // Weighted: Quality Standards 50%, Audit Results 30%, SIRS 20%
  const weighted = 
    (qualityStandards || 0) * 0.5 +
    (auditResults || 0) * 0.3 +
    (sirsCompliance || 0) * 0.2;
  
  return Math.round(weighted * 2) / 2;
};

/**
 * Calculate Staffing score
 */
export const calculateStaffingScore = (careMinutesCompliant, rnMinutesCompliant, staffingLevels, turnoverRate) => {
  // Critical: Must meet BOTH total care minutes AND RN care minutes
  const meetsBothTargets = careMinutesCompliant && rnMinutesCompliant;
  
  if (!meetsBothTargets) {
    // If either target not met, max 2 stars
    return Math.min(
      (staffingLevels || 0) * 0.6 + (turnoverRate || 0) * 0.4,
      2
    );
  }
  
  // If both targets met, calculate normally
  const scores = [];
  if (careMinutesCompliant) scores.push(5); // Full points for compliance
  if (rnMinutesCompliant) scores.push(5); // Full points for RN compliance
  if (staffingLevels && typeof staffingLevels === 'number') scores.push(staffingLevels);
  if (turnoverRate && typeof turnoverRate === 'number') scores.push(turnoverRate);
  
  if (scores.length === 0) return 0;
  
  const average = scores.reduce((sum, val) => sum + val, 0) / scores.length;
  return Math.round(average * 2) / 2;
};

/**
 * Calculate Quality Measures score
 */
export const calculateQualityMeasuresScore = (nqipIndicators, clinicalMetrics, safetyIndicators) => {
  const scores = [];
  
  if (nqipIndicators && typeof nqipIndicators === 'number') scores.push(nqipIndicators);
  if (clinicalMetrics && typeof clinicalMetrics === 'number') scores.push(clinicalMetrics);
  if (safetyIndicators && typeof safetyIndicators === 'number') scores.push(safetyIndicators);
  
  if (scores.length === 0) return 0;
  
  // Weighted: NQIP 40%, Clinical 35%, Safety 25%
  const weighted = 
    (nqipIndicators || 0) * 0.4 +
    (clinicalMetrics || 0) * 0.35 +
    (safetyIndicators || 0) * 0.25;
  
  return Math.round(weighted * 2) / 2;
};

export default {
  createStarRating,
  calculateOverallRating,
  calculateComponentScore,
  calculateResidentsExperienceScore,
  calculateComplianceScore,
  calculateStaffingScore,
  calculateQualityMeasuresScore
};
